/**
 * NotificationToast - Toast Notification Component
 * 
 * Displays temporary toast notifications with:
 * - Different styles by type (info, success, warning, error, insight)
 * - Auto-dismiss with progress indicator
 * - Manual dismissal
 * - Optional action button
 */

import { useEffect, useState } from 'react';
import { useNotifications, type ToastNotification } from '../../contexts/NotificationContext';
import './NotificationToast.css';

// ================================
// Icons
// ================================

const icons: Record<ToastNotification['type'], string> = {
  info: 'ℹ️',
  success: '✅',
  warning: '⚠️',
  error: '❌',
  insight: '💡',
};

// ================================
// Single Toast Component
// ================================

interface ToastItemProps {
  toast: ToastNotification;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!toast.duration || toast.duration <= 0) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / toast.duration!) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [toast.duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(onDismiss, 200); // Wait for exit animation
  };

  return (
    <div 
      className={`notification-toast notification-toast--${toast.type} ${isExiting ? 'notification-toast--exiting' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <div className="notification-toast__icon">
        {icons[toast.type]}
      </div>

      <div className="notification-toast__content">
        <div className="notification-toast__title">{toast.title}</div>
        {toast.message && (
          <div className="notification-toast__message">{toast.message}</div>
        )}
        {toast.action && (
          <button 
            className="notification-toast__action"
            onClick={toast.action.onClick}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {toast.dismissible && (
        <button 
          className="notification-toast__dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          ×
        </button>
      )}

      {toast.duration && toast.duration > 0 && (
        <div 
          className="notification-toast__progress"
          style={{ width: `${progress}%` }}
        />
      )}
    </div>
  );
}

// ================================
// Toast Container Component
// ================================

interface NotificationToastContainerProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export function NotificationToastContainer({ 
  position = 'top-right' 
}: NotificationToastContainerProps) {
  const { state, dismissToast } = useNotifications();

  if (state.toasts.length === 0) return null;

  return (
    <div className={`notification-toast-container notification-toast-container--${position}`}>
      {state.toasts.map(toast => (
        <ToastItem 
          key={toast.id} 
          toast={toast} 
          onDismiss={() => dismissToast(toast.id)} 
        />
      ))}
    </div>
  );
}

export default NotificationToastContainer;
